import styles from './ScheduledOrder.module.scss';

import { FC, KeyboardEventHandler, memo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from 'components/Icon';
import { CloseButton } from 'components/CloseButton';
import { ScheduledOrder } from './ScheduledOrder';
import { Button } from '@clover/web-lib-consumer-ui';
import { SCHEDULED_ORDER_IDS } from './ScheduledOrder.consts';
import { useHandleCloseDrawer } from 'hooks/useHandleCloseDrawer';
import { useScheduledOrderDisplay } from 'hooks/context/ScheduledOrderDisplay';
import { useScheduledOrderMethods } from 'hooks/context/ScheduledOrderMethods';
import { useWindowSize } from 'hooks/breakpoints';
import { KEY } from 'util/Input';
import { useCloseNavigate } from 'hooks/useCloseNavigate/useCloseNavigate';

export const ScheduledOrderWrapper: FC<{ onClose: () => void }> = memo(function CartData({ onClose }) {
  const { t } = useTranslation();
  const { updateDate, updateTime } = useScheduledOrderMethods();
  const { displayValueDate, displayValueTime, resetDisplayValueState, resetScheduledOrderToInitialValues } = useScheduledOrderDisplay();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { isDesktop } = useWindowSize();
  const exitCartPreview = useCloseNavigate('cart');

  const handleConfirm = useCallback(() => {
    if (!displayValueDate && !displayValueTime) resetScheduledOrderToInitialValues();

    if (displayValueDate && displayValueTime) {
      updateDate(displayValueDate?.dateTime);
      updateTime(displayValueTime?.dateTime);
    }

    onClose();
  }, [displayValueDate, displayValueTime, onClose, resetScheduledOrderToInitialValues, updateDate, updateTime]);

  const handleClose = useCallback(() => {
    resetDisplayValueState();

    onClose();
  }, [onClose, resetDisplayValueState]);

  const onCrossBtnClick = useCallback(() => {
    resetDisplayValueState();

    exitCartPreview();
  }, [exitCartPreview, resetDisplayValueState]);

  // Handle closing on outside mouse clicks
  useHandleCloseDrawer(wrapperRef, () => { handleClose(); });

  const handleKeyDown: KeyboardEventHandler = useCallback(e => {
    if ((e.key === KEY.ENTER) || (e.key === KEY.SPACE)) handleClose();
  }, [handleClose]);

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <div className={styles.headerContainer}>
        <div className={styles.title}>
            <div className={styles.iconContainer}>
            <Icon
              tabIndex={0}
              className={styles.leftArrowIcon}
              onKeyDown={handleKeyDown}
              name="leftArrow"
              title={t('back')}
              size={16}
              data-testid={SCHEDULED_ORDER_IDS.back}
              role="button"
              onClick={handleClose}
            ></Icon>
            </div>
            <h2 className={styles.header}>{t('scheduledOrder.button')}</h2>
        </div>
        {!isDesktop && <CloseButton onClick={onCrossBtnClick} />}
      </div>
      <ScheduledOrder />
      <div className={styles.buttonWrapper}>
        <Button
          type="button"
          className={styles.confirm}
          onClick={handleConfirm}
          data-testid={SCHEDULED_ORDER_IDS.confirm}
        >
          <span>{t('confirm')}</span>
        </Button>
      </div>
    </div>
  );
});
